import {
  ExtendedDailyParticipant,
  useParticipantIds,
} from '@daily-co/daily-react';
import { useCallback } from 'react';
import { atom, useRecoilCallback, useRecoilValue } from 'recoil';

import {
  BreakoutsSessionData,
  useBreakouts,
} from '../../../contexts/BreakoutsProvider';
import { MAIN_ROOM_IDENTIFIER } from '../helpers/constants';

/**
 * Contains key-value pairs, where a key is a participant's session_id
 * and the value is the assigned breakouts room id.
 */
export const roomMappingState = atom<Record<string, any>>({
  key: 'room-mapping',
  default: {},
});

const sortNames = (
  a: ExtendedDailyParticipant,
  b: ExtendedDailyParticipant
) => {
  if (a.local || b.local) return 1;
  if (a.user_name.toLowerCase() > b.user_name.toLowerCase()) return 1;
  if (a.user_name.toLowerCase() < b.user_name.toLowerCase()) return -1;
  return 0;
};

export const useRoomMapping = () => {
  const currentRoomMapping = useRecoilValue(roomMappingState);
  const { moveParticipants } = useBreakouts();
  const allParticipantIds = useParticipantIds({
    sort: sortNames,
  });

  /**
   * Returns a list of participant ids for a breakout room, identified by roomId.
   * Pass MAIN_ROOM_IDENTIFIER to get participants in main room.
   * @param roomId string The identifier for the room.
   * @returns string[]
   */
  const getRoomMappingByRoomId = useCallback(
    (roomId: string) => {
      const assignedIds = Object.entries(currentRoomMapping)
        .filter(([, roomId]) => roomId !== MAIN_ROOM_IDENTIFIER)
        .map(([sessionId]) => sessionId);
      if (roomId === MAIN_ROOM_IDENTIFIER) {
        return allParticipantIds.filter((id) => !assignedIds.includes(id));
      }
      return allParticipantIds.filter(
        (id) => currentRoomMapping?.[id] === roomId
      );
    },
    [allParticipantIds, currentRoomMapping]
  );

  /**
   * Map a participant, identified by sessionId, to a breakout room, identified by roomId.
   * @param sessionId string The participant's sessionId.
   * @param roomId string The breakout room's identifier.
   */
  const updateRoomMapping = useRecoilCallback(
    ({ set }) =>
      (sessionId: string, roomId: string) => {
        set(roomMappingState, (state) => {
          const newState = { ...state };
          newState[sessionId] = roomId;
          return newState;
        });
      },
    []
  );

  /**
   * Resets all assignments. This will cause participants to be forced back into the main room.
   */
  const resetRoomMapping = useRecoilCallback(
    ({ set }) =>
      () => {
        set(roomMappingState, () => ({}));
      },
    []
  );

  const publishRoomMapping = useCallback(() => {
    moveParticipants(currentRoomMapping);
  }, [currentRoomMapping, moveParticipants]);

  /**
   * Updates local room mapping state to what is currently stored in session state.
   */
  const syncRoomMappingFromSessionState = useRecoilCallback(
    ({ set }) =>
      async (data: BreakoutsSessionData = null) => {
        const newMapping = {};
        Object.entries(data?.br?.mapping ?? {}).forEach(
          ([roomId, { users }]) => {
            users.forEach((userId) => {
              newMapping[userId] = roomId;
            });
          }
        );
        set(roomMappingState, newMapping);
      },
    []
  );

  return {
    currentRoomMapping,
    getRoomMappingByRoomId,
    publishRoomMapping,
    resetRoomMapping,
    syncRoomMappingFromSessionState,
    updateRoomMapping,
  };
};
